import '../css/draw.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Button, Container, Spinner, Navbar, NavbarBrand } from 'react-bootstrap';
import { useAuth } from './auth/AuthContext';
import { useCompetition } from './CompetitionContext';
import { PublicRegattaHeader } from './PublicRegattaHeader';
import { createRealtimeMapCoalescer, subscribeToRealtime } from './realtimeHub';
import { compareEventsByDisplayOrder } from '../utils/eventSort';

const PREVIOUS_ROUND_BY_ROUND = {
    F: 'SF',
    SF: 'QF',
    QF: 'R16'
};

function cleanText(text) {
    if (text === null || text === undefined) return 'TBC';
    return String(text).replace(/[\r\n]+/g, ' ').trim() || 'TBC';
}

function formatClubWithCompetitorNo(competitorNo, clubName) {
    const club = cleanText(clubName);
    const number = String(competitorNo || '').trim();
    if (!number) return club;
    if (/^(TBC|BYE)$/i.test(club)) return club;
    if (club.startsWith(`${number} - `)) return club;
    return `${number} - ${club}`;
}

function hasMeaningfulValue(value) {
    if (value === null || value === undefined) return false;
    const text = String(value).trim();
    return text !== '' && text.toUpperCase() !== 'NULL';
}

function formatRaceTime(value) {
    if (!hasMeaningfulValue(value)) return null;
    const text = String(value).trim();
    const match = text.match(/^(\d{1,2}:\d{2})(?::\d{2}(?:\.\d+)?)?(\s*[AP]M)?$/i);
    if (match) {
        const minutes = match[1];
        const suffix = match[2] ? match[2].trim().toUpperCase() : '';
        return suffix ? `${minutes} ${suffix}` : minutes;
    }
    return text;
}

function buildResultText(node) {
    if (node.syntheticBye) {
        return '';
    }

    const parts = [];

    if (hasMeaningfulValue(node.winningTime)) {
        parts.push(`Time: ${String(node.winningTime).trim()}`);
    }

    if (hasMeaningfulValue(node.verdict)) {
        parts.push(`Verdict: ${String(node.verdict).trim()}`);
    }

    return parts.join(' | ');
}

function normalizeCrewName(value) {
    return String(value || '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
}

function inferMissingFeederSlot(targetNode, feederNode) {
    const targetA = normalizeCrewName(targetNode.laneA);
    const targetB = normalizeCrewName(targetNode.laneB);
    const feederWinner = feederNode.winnerA
        ? normalizeCrewName(feederNode.laneA)
        : feederNode.winnerB
            ? normalizeCrewName(feederNode.laneB)
            : '';

    if (feederWinner && feederWinner === targetA) return 'B';
    if (feederWinner && feederWinner === targetB) return 'A';

    const feederA = normalizeCrewName(feederNode.laneA);
    const feederB = normalizeCrewName(feederNode.laneB);
    if (feederA && (feederA === targetA || feederB === targetA)) return 'B';
    if (feederA && (feederA === targetB || feederB === targetB)) return 'A';

    return 'A';
}

function previousRoundLabel(round) {
    const normalized = String(round || '').trim().toUpperCase();
    return PREVIOUS_ROUND_BY_ROUND[normalized] || `Pre-${round || 'Round'}`;
}

function parseRaceNo(value) {
    const asNumber = Number(value);
    return Number.isFinite(asNumber) ? asNumber : Number.MAX_SAFE_INTEGER;
}

function getDirectBladeUrl(value) {
    const text = String(value || '').trim();
    const proxyMatch = text.match(/\/api\/clubs\/(\d+)\/blade(?:\?.*)?$/i);
    if (proxyMatch) {
        return `https://webapi.britishrowing.org/blades?id=${proxyMatch[1]}`;
    }

    const directMatch = text.match(/https?:\/\/webapi\.britishrowing\.org\/blades\?id=(\d+)/i);
    if (directMatch) {
        return `https://webapi.britishrowing.org/blades?id=${directMatch[1]}`;
    }

    return null;
}

function shouldTryDirectBladeFallback(src) {
    const isDevHost = /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);
    if (!isDevHost) return false;
    return /\/api\/clubs\/\d+\/blade(?:\?.*)?$/i.test(String(src || '').trim());
}

function normalizeBladeImages(bladeImages, bladeImage) {
    const values = [];
    if (Array.isArray(bladeImages)) values.push(...bladeImages);
    if (bladeImage) values.push(bladeImage);

    return Array.from(new Set(values
        .map(value => String(value || '').trim())
        .filter(Boolean)));
}

function getRoundStageHint(round) {
    const normalized = String(round || '').trim().toUpperCase();
    if (!normalized) return null;

    const known = {
        R128: 0,
        R64: 1,
        R32: 2,
        R16: 3,
        R1: 3,
        QF: 4,
        SF: 5,
        F: 6
    };

    if (Object.prototype.hasOwnProperty.call(known, normalized)) {
        return known[normalized];
    }

    const roundNumberMatch = normalized.match(/^ROUND\s*(\d+)$/);
    if (roundNumberMatch) {
        const roundValue = Number(roundNumberMatch[1]);
        return Number.isFinite(roundValue) ? roundValue : null;
    }

    return null;
}

function toTitleCaseWords(value) {
    const titleCased = String(value || '')
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

    return titleCased
        .replace(/\bmas\s*([a-j](?:\s*\/\s*[a-j])*)\b/gi, (_, category) => `Mas${String(category).replace(/\s+/g, '').toUpperCase()}`)
        .replace(/\bmas([a-j](?:\/[a-j])*)\b/gi, (_, category) => `Mas${String(category).toUpperCase()}`)
        .replace(/\b(i|ii|iii|iv|v|vi|vii|viii|ix|x|xi|xii)\b/gi, token => token.toUpperCase());
}

function sortNodesForDisplay(nodes) {
    return [...nodes].sort((a, b) => {
        const sortOrderA = a.syntheticSortOrder ?? parseRaceNo(a.raceNo);
        const sortOrderB = b.syntheticSortOrder ?? parseRaceNo(b.raceNo);
        const raceNoCompare = sortOrderA - sortOrderB;
        if (raceNoCompare !== 0) return raceNoCompare;
        return a.raceId - b.raceId;
    });
}

function computeStages(nodes, edges) {
    const byId = new Map(nodes.map(node => [node.id, node]));
    const outgoing = new Map();
    const indegree = new Map(nodes.map(node => [node.id, 0]));
    const depth = new Map(nodes.map(node => [node.id, 0]));

    for (const edge of edges) {
        if (!byId.has(edge.from) || !byId.has(edge.to)) continue;
        if (!outgoing.has(edge.from)) outgoing.set(edge.from, []);
        outgoing.get(edge.from).push(edge.to);
        indegree.set(edge.to, (indegree.get(edge.to) || 0) + 1);
    }

    const queue = [];
    for (const node of nodes) {
        if ((indegree.get(node.id) || 0) === 0) queue.push(node.id);
    }

    let cursor = 0;
    while (cursor < queue.length) {
        const currentId = queue[cursor++];
        const nextNodes = outgoing.get(currentId) || [];
        for (const nextId of nextNodes) {
            const nextDepth = (depth.get(currentId) || 0) + 1;
            depth.set(nextId, Math.max(depth.get(nextId) || 0, nextDepth));
            indegree.set(nextId, (indegree.get(nextId) || 0) - 1);
            if ((indegree.get(nextId) || 0) === 0) queue.push(nextId);
        }
    }

    const minRoundHint = nodes.reduce((acc, node) => {
        const hint = getRoundStageHint(node.round);
        if (hint === null) return acc;
        return acc === null ? hint : Math.min(acc, hint);
    }, null);

    const grouped = new Map();
    for (const node of nodes) {
        const depthStage = depth.get(node.id) || 0;
        const roundHint = getRoundStageHint(node.round);
        const hintedStage = roundHint !== null && minRoundHint !== null ? Math.max(0, roundHint - minRoundHint) : null;
        const stage = hintedStage !== null ? Math.max(depthStage, hintedStage) : depthStage;
        if (!grouped.has(stage)) grouped.set(stage, []);
        grouped.get(stage).push(node);
    }

    return [...grouped.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([stage, stageNodes]) => ({
            stage,
            label: (() => {
                const uniqueRounds = [...new Set(stageNodes.map(node => node.round || 'Round'))];
                if (uniqueRounds.length === 1) return uniqueRounds[0];
                return `Round ${stage + 1}`;
            })(),
            nodes: sortNodesForDisplay(stageNodes)
        }));
}

function buildDisplayTreeWithByes(tree) {
    const nodes = tree.nodes.map(node => ({ ...node }));
    const edges = [...tree.edges];
    const nodeById = new Map(nodes.map(node => [node.id, node]));
    const incomingByTo = new Map();

    for (const edge of edges) {
        if (!incomingByTo.has(edge.to)) incomingByTo.set(edge.to, []);
        incomingByTo.get(edge.to).push(edge.from);
    }

    const isWorLabel = value => /^WOR\s+\d+$/i.test(cleanText(value));
    const isPlaceholderValue = value => {
        const text = cleanText(value);
        return text === 'TBC' || isWorLabel(text);
    };

    const swapTargetLanes = (node) => {
        [
            ['laneA', 'laneB'],
            ['laneATitleAbbreviated', 'laneBTitleAbbreviated'],
            ['laneACompetitorNo', 'laneBCompetitorNo'],
            ['laneABladeImages', 'laneBBladeImages'],
            ['laneABladeImage', 'laneBBladeImage'],
            ['laneACrew', 'laneBCrew'],
            ['laneAMastersCategory', 'laneBMastersCategory'],
            ['winnerA', 'winnerB'],
            ['wora', 'worb']
        ].forEach(([a, b]) => {
            const temp = node[a];
            node[a] = node[b];
            node[b] = temp;
        });
    };

    const resolveMissingSlot = (targetNode, feederNode) => {
        if (targetNode.wora != null && Number(targetNode.wora) === Number(feederNode.raceId)) return 'B';
        if (targetNode.worb != null && Number(targetNode.worb) === Number(feederNode.raceId)) return 'A';
        return inferMissingFeederSlot(targetNode, feederNode);
    };

    for (const targetNode of nodes) {
        const incoming = incomingByTo.get(targetNode.id) || [];
        if (incoming.length !== 1) continue;

        const feederNode = nodeById.get(incoming[0]);
        if (!feederNode) continue;

        // When a race has only one real feeder, keep the concrete bye winner in
        // lane A (top) so card order matches prior-round visual order.
        if (isPlaceholderValue(targetNode.laneA) && !isPlaceholderValue(targetNode.laneB)) {
            swapTargetLanes(targetNode);
        }

        let missingSlot = resolveMissingSlot(targetNode, feederNode);

        let byeCrew = missingSlot === 'A' ? cleanText(targetNode.laneA) : cleanText(targetNode.laneB);
        if (!byeCrew || byeCrew === 'TBC' || isWorLabel(byeCrew)) {
            const fallback = missingSlot === 'A' ? cleanText(targetNode.laneB) : cleanText(targetNode.laneA);
            if (fallback && fallback !== 'TBC' && !isWorLabel(fallback)) {
                byeCrew = fallback;
            }
        }
        if (!byeCrew || byeCrew === 'TBC' || isWorLabel(byeCrew)) continue;

        // If fallback picked from the opposite lane, normalize and re-evaluate
        // missing slot so synthetic BYE metadata stays aligned.
        if ((missingSlot === 'A' && cleanText(targetNode.laneA) !== byeCrew)
            || (missingSlot === 'B' && cleanText(targetNode.laneB) !== byeCrew)) {
            swapTargetLanes(targetNode);
            missingSlot = resolveMissingSlot(targetNode, feederNode);
            byeCrew = missingSlot === 'A' ? cleanText(targetNode.laneA) : cleanText(targetNode.laneB);
        }

        const syntheticId = `BYE_${targetNode.id}_${missingSlot}`;
        if (nodeById.has(syntheticId)) continue;

        const syntheticNode = {
            id: syntheticId,
            raceId: `BYE-${targetNode.raceId}-${missingSlot}`,
            raceNo: 'BYE',
            syntheticSortOrder: parseRaceNo(feederNode.raceNo) - 0.5,
            round: previousRoundLabel(targetNode.round),
            time: targetNode.time || null,
            laneA: byeCrew,
            laneATitleAbbreviated: missingSlot === 'A'
                ? cleanText(targetNode.laneATitleAbbreviated || targetNode.laneA)
                : cleanText(targetNode.laneBTitleAbbreviated || targetNode.laneB),
            laneACompetitorNo: missingSlot === 'A' ? targetNode.laneACompetitorNo || '' : targetNode.laneBCompetitorNo || '',
            laneABladeImages: missingSlot === 'A'
                ? normalizeBladeImages(targetNode.laneABladeImages, targetNode.laneABladeImage)
                : normalizeBladeImages(targetNode.laneBBladeImages, targetNode.laneBBladeImage),
            laneABladeImage: missingSlot === 'A' ? targetNode.laneABladeImage || null : targetNode.laneBBladeImage || null,
            laneACrew: missingSlot === 'A' ? targetNode.laneACrew || null : targetNode.laneBCrew || null,
            laneAMastersCategory: missingSlot === 'A' ? targetNode.laneAMastersCategory || null : targetNode.laneBMastersCategory || null,
            laneB: 'BYE',
            laneBTitleAbbreviated: 'BYE',
            laneBCompetitorNo: '',
            laneBBladeImages: [],
            laneBBladeImage: null,
            laneBCrew: null,
            laneBMastersCategory: null,
            handicapEvent: Boolean(targetNode.handicapEvent),
            winnerA: true,
            winnerB: false,
            winningTime: null,
            verdict: 'BYE',
            syntheticBye: true
        };

        const syntheticEdge = {
            from: syntheticId,
            to: targetNode.id,
            label: 'Winner'
        };

        nodes.push(syntheticNode);
        edges.push(syntheticEdge);
        nodeById.set(syntheticId, syntheticNode);
        if (!incomingByTo.has(targetNode.id)) incomingByTo.set(targetNode.id, []);
        incomingByTo.get(targetNode.id).push(syntheticId);
    }

    return {
        ...tree,
        nodes,
        edges
    };
}

function buildEventTree(eventTitle, races) {
    const eventRaceIds = new Set(races.map(r => r.raceId));

    const nodes = races.map(race => ({
        id: `R${race.raceId}`,
        raceId: race.raceId,
        raceNo: race.raceNo,
        round: race.round,
        time: race.raceTimeFormatted || race.raceTime,
        laneA: cleanText(race.laneA?.title),
        laneATitleAbbreviated: cleanText(race.laneA?.titleAbbreviated),
        laneACompetitorNo: String(race.laneA?.competitorNo || '').trim(),
        laneABladeImages: normalizeBladeImages(race.laneA?.bladeImages, race.laneA?.bladeImage),
        laneABladeImage: race.laneA?.bladeImage || null,
        laneACrew: race.laneA?.suffix ? cleanText(race.laneA.suffix) : null,
        laneAMastersCategory: race.laneA?.mastersCategory ? cleanText(`Mas${race.laneA.mastersCategory}`) : null,
        laneB: cleanText(race.laneB?.title),
        laneBTitleAbbreviated: cleanText(race.laneB?.titleAbbreviated),
        laneBCompetitorNo: String(race.laneB?.competitorNo || '').trim(),
        laneBBladeImages: normalizeBladeImages(race.laneB?.bladeImages, race.laneB?.bladeImage),
        laneBBladeImage: race.laneB?.bladeImage || null,
        laneBCrew: race.laneB?.suffix ? cleanText(race.laneB.suffix) : null,
        laneBMastersCategory: race.laneB?.mastersCategory ? cleanText(`Mas${race.laneB.mastersCategory}`) : null,
        handicapEvent: Boolean(race.event?.handicapEvent),
        wora: race.wora ?? null,
        worb: race.worb ?? null,
        winnerA: Boolean(race.laneA?.winner),
        winnerB: Boolean(race.laneB?.winner),
        winningTime: race.winningTime,
        verdict: race.verdict
    }));

    const edges = races
        .filter(race => race.nextRaceId !== null && race.nextRaceId !== undefined && eventRaceIds.has(race.nextRaceId))
        .map(race => {
            let label = 'Winner';
            if (race.laneA?.winner) label = 'Winner A';
            if (race.laneB?.winner) label = 'Winner B';

            return {
                from: `R${race.raceId}`,
                to: `R${race.nextRaceId}`,
                label
            };
        });

    return {
        eventTitle,
        nodes,
        edges
    };
}

function EventBracket({ tree, showMastersCategory }) {
    const diagramWrapRef = useRef(null);
    const canvasRef = useRef(null);
    const bracketRef = useRef(null);
    const connectorLayerRef = useRef(null);
    const displayTree = useMemo(() => buildDisplayTreeWithByes(tree), [tree]);
    const stages = useMemo(() => computeStages(displayTree.nodes, displayTree.edges), [displayTree]);

    useEffect(() => {
        const layoutBracket = () => {
            const bracket = bracketRef.current;
            if (!bracket) return;

            const incomingByTo = new Map();
            for (const edge of displayTree.edges) {
                if (!incomingByTo.has(edge.to)) incomingByTo.set(edge.to, []);
                incomingByTo.get(edge.to).push(edge.from);
            }

            const centerYByNodeId = new Map();
            const stageGap = 10;
            let maxStageHeight = 0;

            const stageMatchesElements = bracket.querySelectorAll('.stage-matches');

            for (let stageIndex = 0; stageIndex < stages.length; stageIndex += 1) {
                const stage = stages[stageIndex];
                const placements = [];
                let naturalCursor = 0;

                for (const node of stage.nodes) {
                    const card = bracket.querySelector(`[data-node-id="${node.id}"]`);
                    if (!card) continue;

                    const cardHeight = card.offsetHeight;
                    let desiredTop = naturalCursor;
                    let hasFeederContext = false;

                    if (stageIndex > 0) {
                        const feeders = (incomingByTo.get(node.id) || []).filter(feederId => centerYByNodeId.has(feederId));
                        if (feeders.length > 0) {
                            hasFeederContext = true;
                            const averageFeederCenter =
                                feeders.reduce((sum, feederId) => sum + centerYByNodeId.get(feederId), 0) / feeders.length;
                            desiredTop = averageFeederCenter - cardHeight / 2;
                        }
                    }

                    placements.push({
                        nodeId: node.id,
                        card,
                        cardHeight,
                        desiredTop,
                        hasFeederContext
                    });

                    naturalCursor += cardHeight + stageGap;
                }

                placements.sort((a, b) => {
                    if (a.hasFeederContext && !b.hasFeederContext) return -1;
                    if (!a.hasFeederContext && b.hasFeederContext) return 1;
                    return a.desiredTop - b.desiredTop;
                });

                let nextAllowedTop = 0;
                let stageHeight = 0;

                for (const placement of placements) {
                    const top = Math.max(placement.desiredTop, nextAllowedTop);
                    placement.card.style.top = `${top}px`;

                    const center = top + placement.cardHeight / 2;
                    centerYByNodeId.set(placement.nodeId, center);

                    nextAllowedTop = top + placement.cardHeight + stageGap;
                    stageHeight = Math.max(stageHeight, top + placement.cardHeight);
                }

                maxStageHeight = Math.max(maxStageHeight, stageHeight);
            }

            const bracketHeight = Math.max(120, Math.ceil(maxStageHeight));
            for (const matchesElement of stageMatchesElements) {
                matchesElement.style.height = `${bracketHeight}px`;
            }
        };

        const applyBracketScale = () => {
            const canvas = canvasRef.current;
            const diagramWrap = diagramWrapRef.current;
            const bracket = bracketRef.current;
            if (!canvas || !diagramWrap || !bracket) return;

            canvas.style.zoom = '';

            const style = getComputedStyle(diagramWrap);
            const paddingH = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
            const availableWidth = diagramWrap.clientWidth - paddingH;
            const naturalWidth = bracket.scrollWidth;

            if (naturalWidth > availableWidth) {
                canvas.style.zoom = String(availableWidth / naturalWidth);
            }
        };

        const drawConnectors = () => {
            const bracket = bracketRef.current;
            const connectorLayer = connectorLayerRef.current;
            if (!connectorLayer || !bracket) return;

            connectorLayer.innerHTML = '';

            const width = Math.max(bracket.scrollWidth, bracket.clientWidth);
            const height = Math.max(bracket.scrollHeight, bracket.clientHeight);

            connectorLayer.setAttribute('width', String(width));
            connectorLayer.setAttribute('height', String(height));
            connectorLayer.setAttribute('viewBox', `0 0 ${width} ${height}`);

            const bracketRect = bracket.getBoundingClientRect();
            const zoomFactor = bracket.offsetWidth ? bracketRect.width / bracket.offsetWidth : 1;

            for (const edge of displayTree.edges) {
                const fromNode = bracket.querySelector(`[data-node-id="${edge.from}"]`);
                const toNode = bracket.querySelector(`[data-node-id="${edge.to}"]`);
                if (!fromNode || !toNode) continue;

                const fromRect = fromNode.getBoundingClientRect();
                const toRect = toNode.getBoundingClientRect();

                const startX = (fromRect.right - bracketRect.left) / zoomFactor;
                const startY = (fromRect.top - bracketRect.top + fromRect.height / 2) / zoomFactor;
                const endX = (toRect.left - bracketRect.left) / zoomFactor;
                const endY = (toRect.top - bracketRect.top + toRect.height / 2) / zoomFactor;
                const deltaX = Math.max(20, (endX - startX) * 0.5);

                const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                path.setAttribute(
                    'd',
                    `M ${startX} ${startY} C ${startX + deltaX} ${startY}, ${endX - deltaX} ${endY}, ${endX} ${endY}`
                );
                path.setAttribute('fill', 'none');
                path.setAttribute('stroke', '#b8bdc4');
                path.setAttribute('stroke-width', '1.7');
                path.setAttribute('stroke-linecap', 'round');
                path.setAttribute('stroke-opacity', '0.95');
                connectorLayer.appendChild(path);
            }
        };

        const relayout = () => {
            layoutBracket();
            applyBracketScale();
            drawConnectors();
        };

        const frame = requestAnimationFrame(relayout);
        const onResize = () => requestAnimationFrame(relayout);
        window.addEventListener('resize', onResize);
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('resize', onResize);
        };
    }, [displayTree, stages]);

    const renderBladeImages = (images, lane, node, clubName) => {
        const bladeSources = normalizeBladeImages(images, null);
        if (!bladeSources.length) return null;

        return (
            <span className="draw-club-blades" aria-hidden="true">
                {bladeSources.map((src, index) => (
                    <img
                        key={`${lane}-${src}-${index}`}
                        className="draw-club-blade"
                        src={src}
                        alt=""
                        loading="lazy"
                        onLoad={() => {
                            console.debug('[Draw Blade] Loaded', {
                                raceId: node.raceId,
                                raceNo: node.raceNo,
                                lane,
                                club: clubName,
                                src
                            });
                        }}
                        onError={(event) => {
                            const failedSrc = event.currentTarget.currentSrc || src;
                            const fallbackSrc = getDirectBladeUrl(failedSrc);
                            const hasTriedFallback = event.currentTarget.dataset.fallbackTried === '1';
                            if (fallbackSrc && !hasTriedFallback && shouldTryDirectBladeFallback(failedSrc)) {
                                event.currentTarget.dataset.fallbackTried = '1';
                                console.warn('[Draw Blade] Proxy failed, trying direct source', {
                                    raceId: node.raceId,
                                    raceNo: node.raceNo,
                                    lane,
                                    club: clubName,
                                    failedSrc,
                                    fallbackSrc
                                });
                                event.currentTarget.src = fallbackSrc;
                                return;
                            }
                            console.debug('[Draw Blade] Missing or unavailable', {
                                raceId: node.raceId,
                                raceNo: node.raceNo,
                                lane,
                                club: clubName,
                                src: failedSrc
                            });
                            event.currentTarget.style.display = 'none';
                        }}
                    />
                ))}
            </span>
        );
    };

    return (
        <div className="diagram-wrap" ref={diagramWrapRef}>
            <div className="bracket-canvas" ref={canvasRef}>
                <svg className="connector-layer" aria-hidden="true" ref={connectorLayerRef} />
                <div className={`bracket ${stages.length <= 2 ? 'bracket-few-rounds' : ''}`} ref={bracketRef}>
                    {stages.map(stageData => (
                        <section className="bracket-stage" key={`stage-${stageData.stage}`}>
                            <h3 className="stage-title">{stageData.label}</h3>
                            <div className="stage-matches">
                                {stageData.nodes.map(node => {
                                    const resultText = buildResultText(node);
                                    const raceLabel = `Race ${node.raceNo || node.raceId}`;
                                    const raceTime = node.syntheticBye ? null : formatRaceTime(node.time);

                                    return (
                                        <article
                                            key={node.id}
                                            className={`match-card ${node.winnerA ? 'winner-a' : ''} ${node.winnerB ? 'winner-b' : ''}`.trim()}
                                            data-node-id={node.id}
                                        >
                                            <div className="match-head">{raceTime ? `${raceLabel} - ${raceTime}` : raceLabel}</div>
                                            <div className="match-lines">
                                                <div className="match-line">
                                                    {renderBladeImages(node.laneABladeImages, 'A', node, node.laneA)}
                                                    <span className="draw-club-name-abbrev">{formatClubWithCompetitorNo(node.laneACompetitorNo, node.laneATitleAbbreviated || node.laneA)}</span>
                                                    {(hasMeaningfulValue(node.laneACrew) || (showMastersCategory && node.handicapEvent && hasMeaningfulValue(node.laneAMastersCategory))) && (
                                                        <span className="draw-lane-meta">
                                                            {hasMeaningfulValue(node.laneACrew) && (
                                                                <span className="crew-name">
                                                                    {cleanText(node.laneACrew)}
                                                                </span>
                                                            )}
                                                            {showMastersCategory && node.handicapEvent && hasMeaningfulValue(node.laneAMastersCategory) && (
                                                                <span className="masters-category">
                                                                    {cleanText(node.laneAMastersCategory)}
                                                                </span>
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="match-line">
                                                    {renderBladeImages(node.laneBBladeImages, 'B', node, node.laneB)}
                                                    <span className="draw-club-name-abbrev">{formatClubWithCompetitorNo(node.laneBCompetitorNo, node.laneBTitleAbbreviated || node.laneB)}</span>
                                                    {(hasMeaningfulValue(node.laneBCrew) || (showMastersCategory && node.handicapEvent && hasMeaningfulValue(node.laneBMastersCategory))) && (
                                                        <span className="draw-lane-meta">
                                                            {hasMeaningfulValue(node.laneBCrew) && (
                                                                <span className="crew-name">
                                                                    {cleanText(node.laneBCrew)}
                                                                </span>
                                                            )}
                                                            {showMastersCategory && node.handicapEvent && hasMeaningfulValue(node.laneBMastersCategory) && (
                                                                <span className="masters-category">
                                                                    {cleanText(node.laneBMastersCategory)}
                                                                </span>
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {resultText && <div className="match-result">{resultText}</div>}
                                        </article>
                                    );
                                })}
                            </div>
                        </section>
                    ))}
                </div>
            </div>
        </div>
    );
}

export function Draw() {
    const { ReDrawAvailable } = useCompetition();
    const [races, setRaces] = useState([]);
    const [redrawLoading, setRedrawLoading] = useState({});
    const [selectedClub, setSelectedClub] = useState('');
    const [status, setStatus] = useState('Disconnected.');
    const [lastUpdate, setLastUpdate] = useState('');
    const { isAdmin, getToken, loading: authLoading } = useAuth();

    const canRedrawEvent = isAdmin && ReDrawAvailable;

    const fetchData = useCallback(async () => {
        try {
            const token = getToken();
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };
            const response = await axios.get('/api/timetable', config);
            setRaces(response.data);
        } catch (err) {
            console.error('Error fetching timetable data:', err);
        }
    }, [getToken]);

    useEffect(() => {
        if (authLoading) {
            return undefined;
        }

        fetchData();

        const receiveRaceResultCoalescer = createRealtimeMapCoalescer((events) => {
            setRaces(prevRaces => {
                const _races = [...prevRaces];
                events.forEach(({ competitionId, raceid, winnerid, verdict, timetaken, nextrace }) => {
                    const raceUpdate = _races.find(race => race.raceId === raceid && race.competitionId === competitionId);
                    if (!raceUpdate) return;

                    let winnerInfo;
                    raceUpdate.verdict = verdict;
                    raceUpdate.winningTime = timetaken;
                    raceUpdate.laneA.winner = winnerid === 'a';
                    raceUpdate.laneB.winner = winnerid === 'b';
                    winnerInfo = winnerid === 'a' ? raceUpdate.laneA : raceUpdate.laneB;

                    if (raceUpdate.laneA.winner || raceUpdate.laneB.winner) {
                        raceUpdate.status = 3;
                    }

                    if ((raceUpdate.round !== 'F' || nextrace?.raceId != null) && winnerInfo) {
                        const nextRound = _races.find(race => race.raceId === nextrace?.raceId);
                        if (nextRound) {
                            if (nextRound.wora === raceUpdate.raceId) {
                                nextRound.laneA.title = winnerInfo.title;
                                nextRound.laneA.titleMobile = winnerInfo.titleMobile;
                                nextRound.laneA.titleAbbreviated = winnerInfo.titleAbbreviated;
                                nextRound.laneA.titleFull = winnerInfo.titleFull;
                                nextRound.laneA.competitorNo = winnerInfo.competitorNo;
                                nextRound.laneA.suffix = winnerInfo.suffix;
                                nextRound.laneA.titleCode = winnerInfo.titleCode;
                            }
                            if (nextRound.worb === raceUpdate.raceId) {
                                nextRound.laneB.title = winnerInfo.title;
                                nextRound.laneB.titleMobile = winnerInfo.titleMobile;
                                nextRound.laneB.titleAbbreviated = winnerInfo.titleAbbreviated;
                                nextRound.laneB.titleFull = winnerInfo.titleFull;
                                nextRound.laneB.competitorNo = winnerInfo.competitorNo;
                                nextRound.laneB.suffix = winnerInfo.suffix;
                                nextRound.laneB.titleCode = winnerInfo.titleCode;
                            }
                        }
                    }
                });
                return _races;
            });
        }, (event) => `${event.competitionId}:${event.raceid}`);

        const raceStatusUpdatedCoalescer = createRealtimeMapCoalescer((events) => {
            setRaces(prevRaces => {
                const _races = [...prevRaces];
                events.forEach(({ competitionId, raceId, statusId }) => {
                    const raceUpdate = _races.find(race => race.raceId === raceId && race.competitionId === competitionId);
                    if (raceUpdate) {
                        raceUpdate.status = statusId;
                    }
                });
                return _races;
            });
        }, (event) => `${event.competitionId}:${event.raceId}`);

        const raceMovedCoalescer = createRealtimeMapCoalescer((events) => {
            setRaces(prevRaces => {
                const _races = [...prevRaces];
                events.forEach(({ competitionId, raceid, newraceid }) => {
                    if (_races.length === 0 || _races[0].competitionId !== competitionId) return;

                    const newRaceId = parseInt(newraceid, 10);
                    const raceA = { ..._races.find(race => race.raceId === raceid) };
                    const raceB = { ..._races.find(race => race.raceId === newRaceId) };
                    const destinationRace = _races.find(race => race.raceId === newRaceId);
                    const sourceRace = _races.find(race => race.raceId === raceid);
                    if (!destinationRace || !sourceRace || !raceA || !raceB) return;

                    destinationRace.raceNo = raceA.raceNo;
                    destinationRace.laneA.title = raceA.laneA.title;
                    destinationRace.laneA.titleShort = raceA.laneA.titleShort;
                    destinationRace.laneA.suffix = raceA.laneA.suffix;
                    destinationRace.laneB.title = raceA.laneB.title;
                    destinationRace.laneB.titleShort = raceA.laneB.titleShort;
                    destinationRace.laneB.suffix = raceA.laneB.suffix;
                    destinationRace.event.id = raceA.event.id;
                    destinationRace.event.title = raceA.event.title;
                    destinationRace.event.titleShort = raceA.event.titleShort;
                    destinationRace.round = raceA.round;
                    destinationRace.divsion = raceA.division;
                    destinationRace.nextRaceId = raceA.nextRaceId;

                    sourceRace.raceNo = raceB.raceNo;
                    sourceRace.laneA.title = '';
                    sourceRace.laneB.title = '';
                    sourceRace.laneA.suffix = '';
                    sourceRace.laneB.suffix = '';
                    sourceRace.event.id = -1;
                    sourceRace.event.title = '';
                    sourceRace.event.titleShort = '';
                    sourceRace.round = '';
                    sourceRace.nextRaceId = '';
                });
                return _races;
            });
        }, (event) => `${event.competitionId}:${event.raceid}`);

        const unsubscribe = subscribeToRealtime({
            onStatusChange: (nextStatus, updateTime) => {
                setStatus(nextStatus);
                setLastUpdate(updateTime);
            },
            onReconnected: () => {
                fetchData();
            },
            onReceiveRaceResult: (competitionId, raceid, winnerid, verdict, timetaken, nextrace) => {
                receiveRaceResultCoalescer.push({ competitionId, raceid, winnerid, verdict, timetaken, nextrace });
            },
            onRaceStatusUpdated: (competitionId, raceId, statusId) => {
                raceStatusUpdatedCoalescer.push({ competitionId, raceId, statusId });
            },
            onRaceMoved: (competitionId, raceid, newraceid) => {
                raceMovedCoalescer.push({ competitionId, raceid, newraceid });
            }
        });

        return () => {
            receiveRaceResultCoalescer.dispose();
            raceStatusUpdatedCoalescer.dispose();
            raceMovedCoalescer.dispose();
            unsubscribe();
        };
    }, [authLoading, fetchData]);

    async function handleRedraw(eventId) {
        setRedrawLoading(prev => ({ ...prev, [eventId]: true }));
        try {
            const token = getToken();
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };
            await axios.put(`/api/draw/${eventId}`, {}, config);
            await fetchData();
        } catch (err) {
            console.error('Error redrawing event:', err);
        }
        setRedrawLoading(prev => ({ ...prev, [eventId]: false }));
    }

    const renderEventSection = ({ event, eventId, sponsor, tree, showMastersCategory }) => (
        <section key={eventId} className="event-section">
            <div className="event-toolbar">
                <div className="event-left">
                    <h2 className="eventTitle">{toTitleCaseWords(event.title)}</h2>
                    {canRedrawEvent && (
                        <Button
                            className="noprint"
                            variant="primary"
                            size="sm"
                            onClick={() => handleRedraw(eventId)}
                            disabled={!!redrawLoading[eventId]}
                        >
                            {redrawLoading[eventId] ? <Spinner size="sm" animation="border" /> : 'Redraw Event'}
                        </Button>
                    )}
                    {isAdmin && <div className="noprint event-id-debug">{eventId}</div>}
                </div>
                <div className="event-right">
                    {sponsor && <div className="sponsor">Sponsored By: {sponsor}</div>}
                </div>
            </div>
            <EventBracket tree={tree} showMastersCategory={showMastersCategory} />
        </section>
    );

    const availableClubs = useMemo(() => {
        const clubs = new Set();
        const isWorPlaceholder = (value) => /^WOR\s+\d+$/i.test(String(value || '').trim());
        for (const race of races) {
            if (race?.laneA?.titleFull) {
                const clubName = String(race.laneA.titleFull).trim();
                if (clubName && !isWorPlaceholder(clubName)) clubs.add(clubName);
            }
            if (race?.laneB?.titleFull) {
                const clubName = String(race.laneB.titleFull).trim();
                if (clubName && !isWorPlaceholder(clubName)) clubs.add(clubName);
            }
        }
        return [...clubs].sort((a, b) => a.localeCompare(b));
    }, [races]);

    const groupedEvents = useMemo(() => {
        const uniqueEvents = Object.values(
            races
                .filter(race => race.event?.id !== -1)
                .reduce((acc, race) => {
                    const id = race.event.id;
                    if (!acc[id]) {
                        acc[id] = { ...race.event, sponsorTitle: race.sponsorTitle || '' };
                    }
                    if (race.sponsorTitle) {
                        acc[id].sponsorTitle = race.sponsorTitle;
                    }
                    return acc;
                }, {})
        );

        uniqueEvents.sort(compareEventsByDisplayOrder);

        return uniqueEvents.map(event => {
            let eventRaces = races
                .filter(race => race.event?.id === event.id)
                .sort((a, b) => (Number(a.raceNo) || 0) - (Number(b.raceNo) || 0));

            if (selectedClub) {
                const selected = String(selectedClub).trim().toLowerCase();
                const eventIncludesClub = eventRaces.some(race => {
                    const laneAClub = String(race?.laneA?.titleFull || '').trim().toLowerCase();
                    const laneBClub = String(race?.laneB?.titleFull || '').trim().toLowerCase();
                    return laneAClub === selected || laneBClub === selected;
                });
                if (!eventIncludesClub) {
                    eventRaces = [];
                }
            }

            if (eventRaces.length === 0) return null;

            const mastersCategories = new Set();
            if (Boolean(event.handicapEvent)) {
                eventRaces.forEach(race => {
                    const laneA = String(race?.laneA?.mastersCategory || '').trim().toUpperCase().replace(/\s+/g, '');
                    const laneB = String(race?.laneB?.mastersCategory || '').trim().toUpperCase().replace(/\s+/g, '');
                    if (laneA) mastersCategories.add(laneA);
                    if (laneB) mastersCategories.add(laneB);
                });
            }

            return {
                event,
                eventId: event.id,
                sponsor: event.sponsorTitle || '',
                tree: buildEventTree(event.title, eventRaces),
                showMastersCategory: mastersCategories.size > 1
            };
        }).filter(Boolean);
    }, [races, selectedClub]);

    return (
        <div className="draw-page pb-5 mb-5">
        <Container className="draw-container mt-3">
            <PublicRegattaHeader />
            {availableClubs.length > 0 && (
                <div className="controls noprint">
                    {availableClubs.length > 0 && (
                        <>
                            <label htmlFor="clubFilter">Filter by Club:</label>
                            <select
                                id="clubFilter"
                                value={selectedClub}
                                onChange={(e) => setSelectedClub(e.target.value || '')}
                            >
                                <option value="">(All Clubs)</option>
                                {availableClubs.map(club => (
                                    <option key={club} value={club}>
                                        {club}
                                    </option>
                                ))}
                            </select>
                        </>
                    )}
                </div>
            )}
            <section className="all-events">
                {groupedEvents.map(item => renderEventSection(item))}
            </section>
        </Container>
            <div className="fixed-bottom draw-fixed-bottom">
                <Navbar bg="light" variant="light">
                    <Container>
                        <NavbarBrand></NavbarBrand>
                        <Navbar.Text>
                            <div className="draw-update-status">
                                {status}<br />Updated: {lastUpdate}
                            </div>
                        </Navbar.Text>
                    </Container>
                </Navbar>
            </div>
        </div>
    );
}